/**
 * `usePlatformServices` — live snapshot of the IO Services catalogue.
 *
 * Studios get a filtered view (only `isActive == true`); the admin
 * portal sees everything (`includeInactive: true`) so disabled items
 * can be re-enabled.
 *
 * Sort: category → sortOrder asc → title asc. Sorting on the client
 * because Firestore can't combine two orderBys with an inequality
 * filter without a composite index for every variant.
 */
import {
  collection,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';

import { db } from '@/lib/firebase';
import type {
  PlatformService,
  PlatformServiceCategory,
} from '@/lib/platformServices';

export type UsePlatformServicesResult = {
  data: PlatformService[];
  loading: boolean;
};

export type UsePlatformServicesOptions = {
  /** Admin view — show inactive items too. Default false (studio
   *  browse only shows live services). */
  includeInactive?: boolean;
  /** Optional category filter (client-side). */
  category?: PlatformServiceCategory | null;
  /** Optional case-insensitive substring search. Matches title +
   *  shortDescription + longDescription + features. */
  searchText?: string;
};

export function usePlatformServices(
  opts: UsePlatformServicesOptions = {},
): UsePlatformServicesResult {
  const { includeInactive = false, category = null, searchText } = opts;

  const [all, setAll] = useState<PlatformService[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    // Studios filter on isActive at the query level to keep the
    // payload tight; admin sees everything in one snapshot.
    const ref = includeInactive
      ? collection(db, 'platformServices')
      : query(collection(db, 'platformServices'), where('isActive', '==', true));

    return onSnapshot(
      ref,
      (snap) => {
        const rows: PlatformService[] = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<PlatformService, 'id'>),
        }));
        rows.sort((a, b) => {
          if (a.category !== b.category) {
            return a.category.localeCompare(b.category);
          }
          if ((a.sortOrder ?? 0) !== (b.sortOrder ?? 0)) {
            return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
          }
          return a.title.localeCompare(b.title);
        });
        setAll(rows);
        setLoading(false);
      },
      (err) => {
        console.warn('[usePlatformServices] snapshot error:', err);
        setAll([]);
        setLoading(false);
      },
    );
  }, [includeInactive]);

  const filtered = useMemo(() => {
    const q = (searchText ?? '').trim().toLowerCase();
    return all.filter((it) => {
      if (category && it.category !== category) return false;
      if (q) {
        const blob = [
          it.title,
          it.shortDescription ?? '',
          it.longDescription ?? '',
          (it.features ?? []).join(' '),
          it.priceLabel ?? '',
        ]
          .join(' ')
          .toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
  }, [all, category, searchText]);

  return { data: filtered, loading };
}
